import React, { createContext, useState, useContext, useEffect, useCallback, ReactNode, useMemo, useRef } from "react";
import { supabase } from "@/lib/supabaseClient";
import { Session, User as SupabaseUser, SupabaseClient } from "@supabase/supabase-js";
import { User } from "@/types";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { buildApiUrl } from '../lib/api';
import { log } from '@/lib/logger';

interface LoginInput {
  email: string;
  password: string;
}

export interface AuthContextType {
  user: User | null | undefined;
  session: Session | null;
  isLoading: boolean;
  error: Error | null;
  supabase: SupabaseClient | null;
  login: (input: LoginInput) => Promise<void>;
  logout: () => Promise<void>;
  fetchUserProfile: (supabaseUser?: SupabaseUser | null, accessToken?: string | null) => Promise<User | null>;
  activeTenant: string | null;
  setActiveTenant: (tenantId: string | null) => void;
  availableTenants: Array<{ tenantId: string; companyName: string }> | null;
  setAvailableTenants: (tenants: Array<{ tenantId: string; companyName: string }> | null) => void;
  setActiveTenantContextAndRefetch: (tenantId: string | null) => Promise<void>;
  isSuperAdmin: boolean;
  signInWithPassword: (email: string, password: string) => Promise<void>;
  signUpWithPassword: (email: string, password: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  log.render("AuthProvider");
  const [session, setSession] = useState<Session | null>(null);
  const sessionRef = useRef(session);

  const [user, setUser] = useState<User | null | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [activeTenant, setActiveTenant] = useState<string | null>(null);
  const [availableTenants, setAvailableTenants] = useState<Array<{ tenantId: string; companyName: string }> | null>(null);

  // Add request deduplication
  const activeProfileRequestRef = useRef<Promise<User | null> | null>(null);
  const lastFetchedUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  // Memoize the fetchUserProfile function to prevent recreation
  const fetchUserProfile = useCallback(async (supabaseUserParam?: SupabaseUser | null, accessTokenParam?: string | null): Promise<User | null> => {
    log.auth("fetchUserProfile called", { userEmail: supabaseUserParam?.email });
    
    const currentSessionFromRef = sessionRef.current;

    // Determine which Supabase user and token to use
    const currentSupabaseUser = supabaseUserParam ?? currentSessionFromRef?.user ?? (await supabase.auth.getUser()).data.user;
    const currentAccessToken = accessTokenParam ?? currentSessionFromRef?.access_token;

    if (!currentSupabaseUser || !currentAccessToken) {
      log.debug("No Supabase user or access token, returning null", "AUTH");
      setUser(null);
      return null;
    }

    // Request deduplication: if there's already an active request for the same user, return it
    if (activeProfileRequestRef.current && lastFetchedUserIdRef.current === currentSupabaseUser.id) {
      log.debug("Request already in progress, returning existing promise", "AUTH");
      return activeProfileRequestRef.current;
    }

    // Create the request promise and store it
    const requestPromise = (async (): Promise<User | null> => {
      try {
        // Use robust API URL building
        const apiUrl = buildApiUrl('/api/users/profile');
        log.api("GET", apiUrl);
        
        const response = await fetch(apiUrl, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${currentAccessToken}`
          }
        });

        if (!response.ok) {
          if (response.status === 404 && response.statusText === 'Not Found') {
            // If this is a new user who hasn't completed onboarding yet, this is expected
            log.debug("Profile not found, might be a new user", "AUTH");
            setUser(null);
            return null;
          }
          
          if (response.status === 401) {
            log.warn("Unauthorized fetching profile - token might be invalid", "AUTH");
            setUser(null);
            return null;
          }
          
          const errorData = await response.json().catch(() => ({}));
          throw new Error(`Failed to fetch user profile from API: ${response.statusText} ${errorData.message || ''}`);
        }

        const profileData = await response.json();
        log.debug("Profile data received from API", "AUTH", { userId: profileData?.userId });
        
        if (profileData) {
          // Create the user object from the API response
          const appUser: User = {
            userId: profileData.userId,
            email: currentSupabaseUser.email || null,
            fullName: profileData.fullName,
            role: profileData.role,
            tenantId: profileData.tenantId,
            isSuperAdmin: profileData.isSuperAdmin || profileData.role === 'SuperAdmin',
            activeTenantContext: profileData.activeTenantContext || profileData.tenantId,
            lastSignInAt: currentSupabaseUser.last_sign_in_at || null, // Ensures User type compliance
          };
          
          log.debug("Constructed user object", "AUTH", { userId: appUser.userId, role: appUser.role });

          // Deep comparison to avoid unnecessary setUser calls
          setUser(prevUser => {
            if (prevUser && 
                prevUser.userId === appUser.userId &&
                prevUser.email === appUser.email &&
                prevUser.fullName === appUser.fullName &&
                prevUser.role === appUser.role &&
                prevUser.tenantId === appUser.tenantId &&
                prevUser.isSuperAdmin === appUser.isSuperAdmin &&
                prevUser.activeTenantContext === appUser.activeTenantContext &&
                prevUser.lastSignInAt === appUser.lastSignInAt
            ) {
              log.dev("Profile data unchanged, not updating user state", "AUTH");
              return prevUser; // Return previous user to prevent state change
            } else {
              log.debug("Profile data changed, updating user state", "AUTH");
              return appUser;
            }
          });
          
          if (appUser.activeTenantContext) {
            setActiveTenant(appUser.activeTenantContext);
          }
          
          // Save superAdmin status to localStorage for use in API calls
          if (appUser.isSuperAdmin) {
            localStorage.setItem('isSuperAdmin', 'true');
            if (appUser.activeTenantContext) {
              localStorage.setItem('superAdminActiveTenantContext', appUser.activeTenantContext);
            } else {
              localStorage.setItem('superAdminActiveTenantContext', '__NULL__');
            }
          } else {
            localStorage.removeItem('isSuperAdmin');
            localStorage.removeItem('superAdminActiveTenantContext');
          }
          
          return appUser;
        } else {
          log.debug("No profile data in API response, setting user to null", "AUTH");
          setUser(null); 
          return null;
        }
      } catch (e) {
        log.error("Failed to fetch user profile", "AUTH", e);
        setError(e instanceof Error ? e : new Error('Failed to fetch user profile'));
        setUser(null);
        return null;
      } finally {
        // Clear the active request when done
        activeProfileRequestRef.current = null;
        lastFetchedUserIdRef.current = null;
      }
    })();

    // Store the request promise and user ID
    activeProfileRequestRef.current = requestPromise;
    lastFetchedUserIdRef.current = currentSupabaseUser.id;

    return requestPromise;
  }, []); // Remove user dependency to prevent recreation

  // Memoize other callback functions to prevent recreation
  const setActiveTenantContextAndRefetch = useCallback(async (newTenantId: string | null) => {
    log.debug("Setting active tenant context", "AUTH", { newTenantId });

    if (!user) {
      log.error("No user found, cannot set tenant context", "AUTH");
      return;
    }

    try {
      // Update the active tenant in the component state
      setActiveTenant(newTenantId);

      if (user.isSuperAdmin) {
        // For SuperAdmin, save the context for API calls
        if (newTenantId) {
          localStorage.setItem('superAdminActiveTenantContext', newTenantId);
          log.debug("Set localStorage tenant context", "AUTH", { newTenantId });
        } else {
          localStorage.setItem('superAdminActiveTenantContext', '__NULL__');
          log.debug("Set localStorage tenant context to __NULL__", "AUTH");
        }
        
        // IMMEDIATELY update the user object so API calls work right away
        log.debug("Updating user object tenant context", "AUTH", { 
          oldContext: user.activeTenantContext, 
          newContext: newTenantId 
        });
        setUser({
          ...user,
          activeTenantContext: newTenantId
        });
      }

      // Call the API to persist the choice in the database
      const apiUrl = buildApiUrl('/api/users/set-active-tenant');
      const accessToken = (await supabase.auth.getSession()).data.session?.access_token;
      
      if (!accessToken) {
        throw new Error('No access token available to update tenant context.');
      }

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({ activeTenantContext: newTenantId })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to update tenant context: ${response.statusText} - ${errorText}`);
      }

      // Invalidate queries to refetch data with new context
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['claims'] });
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
      
      toast({
        title: "Tenant Context Updated",
        description: newTenantId ? "Tenant context updated successfully." : "Platform view activated successfully.",
        variant: "default",
      });
    } catch (err) {
      log.error("Error switching tenant context", "AUTH", err);
      setError(err as Error);
      toast({
        title: "Error Switching Tenant",
        description: (err as Error).message,
        variant: "destructive",
      });
    }
  }, [user, queryClient, toast]);

  const login = useCallback(async ({ email, password }: LoginInput) => {
    log.auth("Attempting login", { email });
    try {
      setIsLoading(true);
      setError(null);
      
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        log.error("Error signing in", "AUTH", error);
        setError(error);
        toast({
          title: 'Login Failed',
          description: error.message,
          variant: 'destructive',
          duration: 5000,
        });
        setIsLoading(false);
        return;
      }

      // Auth state change will trigger profile fetch automatically
      // No need to call fetchUserProfile here to avoid redundant calls
      log.debug("Sign in successful, auth state change will handle profile fetch", "AUTH");
      
      toast({
        title: 'Login Successful',
        description: `Signing you in...`,
        variant: 'default',
        duration: 3000,
      });
    } catch (error) {
      log.error("Unexpected error during login", "AUTH", error);
      setError(error instanceof Error ? error : new Error('An unexpected error occurred during login'));
      toast({
        title: 'Login Failed',
        description: error instanceof Error ? error.message : 'An unexpected error occurred',
        variant: 'destructive',
        duration: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  }, [supabase, toast]);

  const logout = useCallback(async () => {
    try {
      setIsLoading(true);
      await supabase.auth.signOut();
      // Clear user data
      setUser(null);
      setActiveTenant(null);
      setAvailableTenants(null);
      localStorage.removeItem('isSuperAdmin');
      localStorage.removeItem('superAdminActiveTenantContext');
      
      toast({
        title: "Signed Out",
        description: "You have been successfully signed out.",
        variant: "default",
      });
    } catch (error) {
      log.error("Error during logout", "AUTH", error);
      setError(error instanceof Error ? error : new Error('Failed to sign out'));
      toast({
        title: "Sign Out Failed",
        description: "There was a problem signing you out.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [supabase, toast]);

  // Clean up the auth initialization effect
  useEffect(() => {
    log.debug("Initializing auth state", "AUTH");
    let lastProcessedUserId: string | null = null;
    
    const initAuth = async () => {
      try {
        const { data: { session: initialSession } } = await supabase.auth.getSession();
        setSession(initialSession);
        sessionRef.current = initialSession;
        
        if (initialSession?.user) {
          log.debug("Session fetched", "AUTH", { userEmail: initialSession.user.email });
          lastProcessedUserId = initialSession.user.id;
          await fetchUserProfile(initialSession.user);
        } else {
          setUser(null);
          setIsLoading(false);
        }
      } catch (error) {
        log.error("Error during auth initialization", "AUTH", error);
        setUser(null);
        setIsLoading(false);
      }
    };
    
    initAuth();
    
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, currentSession) => {
      log.auth("Auth state change", { event, userEmail: currentSession?.user?.email });
      setSession(currentSession);
      sessionRef.current = currentSession;
      
      if (event === 'SIGNED_OUT') {
        setUser(null);
        setActiveTenant(null);
        localStorage.removeItem('isSuperAdmin');
        localStorage.removeItem('superAdminActiveTenantContext');
        setIsLoading(false);
        lastProcessedUserId = null;
      } else if (currentSession?.user && (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'USER_UPDATED')) {
        log.debug("Calling fetchUserProfile for auth event", "AUTH", { event });
        lastProcessedUserId = currentSession.user.id;
        await fetchUserProfile(currentSession.user);
        setIsLoading(false);
      } else if (event === 'INITIAL_SESSION' && currentSession?.user) {
        // Only process INITIAL_SESSION if we haven't already processed this user via SIGNED_IN or getSession
        if (lastProcessedUserId !== currentSession.user.id) {
          log.debug("Processing INITIAL_SESSION for new user", "AUTH", { userEmail: currentSession.user.email });
          lastProcessedUserId = currentSession.user.id;
          await fetchUserProfile(currentSession.user);
        } else {
          log.dev("Skipping INITIAL_SESSION - user already processed", "AUTH", { userEmail: currentSession.user.email });
        }
        setIsLoading(false);
      } else if (!currentSession?.user) {
        setUser(null);
        setActiveTenant(null);
        localStorage.removeItem('isSuperAdmin');
        localStorage.removeItem('superAdminActiveTenantContext');
        setIsLoading(false);
        lastProcessedUserId = null;
      }
    });
    
    return () => {
      subscription.unsubscribe();
    };
  }, [fetchUserProfile]); // Include fetchUserProfile in dependencies since it's stable now

  const signInWithPassword = useCallback(async (email: string, password: string) => {
    setIsLoading(true);
    setError(null);
    const { error: signInError } = await supabase.auth.signInWithPassword({ email, password });
    if (signInError) {
      setError(signInError);
      toast({ title: "Sign In Error", description: signInError.message, variant: "destructive" });
    }
    // Auth state change will trigger useEffect to fetch profile
    setIsLoading(false);
  }, [toast]);

  const signUpWithPassword = useCallback(async (email: string, password: string) => {
    setIsLoading(true);
    setError(null);
    const { error: signUpError } = await supabase.auth.signUp({ email, password });
    if (signUpError) {
      setError(signUpError);
      toast({ title: "Sign Up Error", description: signUpError.message, variant: "destructive" });
    } else {
      toast({ title: "Success", description: "Please check your email to confirm your account." });
    }
    // Auth state change might trigger useEffect
    setIsLoading(false);
  }, [toast]);

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    user,
    session: sessionRef.current,
    isLoading,
    error,
    supabase,
    login,
    logout,
    fetchUserProfile,
    activeTenant,
    setActiveTenant,
    availableTenants,
    setAvailableTenants,
    setActiveTenantContextAndRefetch,
    isSuperAdmin: user?.isSuperAdmin || false,
    signInWithPassword,
    signUpWithPassword,
  }), [
    user,
    isLoading,
    error,
    login,
    logout,
    fetchUserProfile,
    activeTenant,
    availableTenants,
    setActiveTenantContextAndRefetch,
    signInWithPassword,
    signUpWithPassword,
  ]);

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
